// Interactive forms (AcroForm, ISO 32000-1 §12.7): reading the fields and their widgets, and saving filled-in values as an incremental
// update with new appearance streams so any reader shows what was typed.

import { PdfDocument, textString } from "./document";
import { Dict, PName, PRef, type PObj } from "./parse";
import { serialize } from "./writer";
import type { FieldValidation } from "./formdesign";

export type FormKind = "text" | "check" | "radio" | "combo" | "list" | "button" | "signature";

export interface FormWidget {
    /** The widget annotation's object number. */
    id: number;
    /** The field the widget belongs to: its fully qualified name. */
    name: string;
    kind: FormKind;
    /** 0-based page. */
    page: number;
    rect: [number, number, number, number];
    /** Text, or the chosen option's export value; "Off" or the on-state name for check boxes and radio buttons. */
    value: string;
    /** The state name this widget turns on (check boxes, radio buttons). */
    onState: string;
    multiline: boolean;
    password: boolean;
    readOnly: boolean;
    required: boolean;
    maxLen: number;
    validation: FieldValidation | undefined;
    options: Array<{ value: string; label: string }>;
    fontSize: number;
    align: 0 | 1 | 2;
}

const FF_READONLY = 1, FF_REQUIRED = 2, FF_MULTILINE = 1 << 12, FF_PASSWORD = 1 << 13, FF_RADIO = 1 << 15, FF_PUSH = 1 << 16, FF_COMBO = 1 << 17;

interface Inherited { ft: string | null; ff: number; v: PObj; da: string | null; q: number; opt: PObj[] | null; maxLen: number; name: string }

/** Every widget of every field in the document. */
export function formWidgets(doc: PdfDocument): FormWidget[] {
    const form = doc.catalog.dict("AcroForm");
    const fields = form?.arr("Fields");
    if (!form || !fields) return [];
    const xref = doc.xref;
    doc.pages();
    const pageOf = new Map<number, number>();
    doc.pageRefs.forEach((r, i) => { if (r) pageOf.set(r.num, i); });
    const out: FormWidget[] = [];
    const seen = new Set<Dict>();
    const defaultDa = form.str("DA");
    const defaultQ = form.num("Q", 0);

    const walk = (node: PObj, ref: PRef | null, parent: Inherited, depth: number) => {
        const d = xref.resolve(node);
        if (!(d instanceof Dict) || seen.has(d) || depth > 24) return;
        seen.add(d);
        const t = d.get("T");
        const own = typeof t === "string" ? textString(t) : "";
        const here: Inherited = {
            ft: d.name("FT") ?? parent.ft,
            ff: d.has("Ff") ? d.num("Ff") : parent.ff,
            v: d.has("V") ? d.get("V") : parent.v,
            da: d.str("DA") ?? parent.da,
            q: d.has("Q") ? d.num("Q") : parent.q,
            opt: d.arr("Opt") ?? parent.opt,
            maxLen: d.has("MaxLen") ? d.num("MaxLen") : parent.maxLen,
            name: own ? (parent.name ? `${parent.name}.${own}` : own) : parent.name
        };
        const kids = d.arr("Kids");
        const kidDicts = kids?.map((k) => xref.resolve(k)) ?? [];
        const widgetKids = kidDicts.some((k) => k instanceof Dict && k.name("Subtype") === "Widget" && !k.has("T"));
        if (kids && !widgetKids) {
            for (const k of kids) walk(k, k instanceof PRef ? k : null, here, depth + 1);
            return;
        }
        const widgets: Array<[Dict, PRef | null]> = widgetKids ? kids!.map((k, i): [Dict, PRef | null] => [kidDicts[i] as Dict, k instanceof PRef ? k : null]).filter((x) => x[0] instanceof Dict) : [[d, ref]];
        for (const [w, wref] of widgets) {
            if (w.name("Subtype") !== "Widget" || !wref) continue;
            if ((w.num("F") & 2) !== 0) continue; // a hidden widget (a removed field) is not shown
            const r = (w.arr("Rect") ?? []).map((x) => { const v = xref.resolve(x); return typeof v === "number" ? v : 0; });
            if (r.length < 4) continue;
            const pref = w.raw("P");
            let page = pref instanceof PRef ? (pageOf.get(pref.num) ?? -1) : -1;
            if (page < 0) { // no /P: find the page that lists the widget
                for (let i = 0; i < doc.numPages && page < 0; i++) for (const a of doc.page(i).dict.arr("Annots") ?? []) if (a instanceof PRef && a.num === wref.num) { page = i; break; }
            }
            if (page < 0) continue;
            const ff = here.ff;
            const kind: FormKind = here.ft === "Tx" ? "text" : here.ft === "Ch" ? (ff & FF_COMBO ? "combo" : "list") : here.ft === "Sig" ? "signature" : here.ft === "Btn" ? (ff & FF_PUSH ? "button" : ff & FF_RADIO ? "radio" : "check") : "text";
            const apN = w.dict("AP")?.get("N");
            let onState = "Yes";
            if (apN instanceof Dict) for (const k of apN.keys()) if (k !== "Off") { onState = k; break; }
            const asName = w.name("AS");
            let value = "";
            const v = here.v;
            if (kind === "check" || kind === "radio") value = v instanceof PName ? (v.name === onState ? onState : "Off") : asName && asName !== "Off" && !(v instanceof PName) ? asName : "Off";
            else if (typeof v === "string") value = textString(v);
            else if (Array.isArray(v) && typeof xref.resolve(v[0]) === "string") value = textString(xref.resolve(v[0]));
            const options = (here.opt ?? []).map((o) => {
                const x = xref.resolve(o);
                if (Array.isArray(x)) return { value: textString(xref.resolve(x[0])), label: textString(xref.resolve(x[1])) };
                const s = textString(x);
                return { value: s, label: s };
            });
            const da = (w.str("DA") ?? here.da ?? defaultDa) || "";
            const fs = /(\d+(?:\.\d+)?)\s+Tf/.exec(da);
            out.push({
                id: wref.num, name: here.name, kind, page, rect: [Math.min(r[0]!, r[2]!), Math.min(r[1]!, r[3]!), Math.max(r[0]!, r[2]!), Math.max(r[1]!, r[3]!)],
                value, onState, multiline: Boolean(ff & FF_MULTILINE), password: Boolean(ff & FF_PASSWORD), readOnly: Boolean(ff & FF_READONLY) || (w.num("F") & 64) !== 0, required: Boolean(ff & FF_REQUIRED),
                maxLen: here.maxLen, options, fontSize: fs ? parseFloat(fs[1]!) : 0, validation: (() => { try { const j = w.str("FGValidate"); return j ? (JSON.parse(j) as FieldValidation) : undefined; } catch { return undefined; } })(), align: ((w.has("Q") ? w.num("Q") : here.q ?? defaultQ) as 0 | 1 | 2) || 0
            });
        }
    };
    const root: Inherited = { ft: null, ff: 0, v: null, da: null, q: defaultQ, opt: null, maxLen: 0, name: "" };
    for (const f of fields) walk(f, f instanceof PRef ? f : null, root, 0);
    return out;
}

// ---- editing the properties of fields that are already in the file -----------------------------------------------------------------

/** What Prepare Form changed on one widget (and so on its field). Removing a field hides its widget for good. */
export interface FieldEdit {
    name?: string;
    label?: string;
    required?: boolean;
    readOnly?: boolean;
    multiline?: boolean;
    maxLen?: number;
    options?: string[];
    validation?: FieldValidation | null;
    remove?: boolean;
}

/** Writes `edits` (widget object number → change) as an incremental update: the field's name, tooltip, flags, length limit, options and rule. */
export function writeFieldEdits(doc: PdfDocument, edits: Map<number, FieldEdit>, api: { writeObj(num: number, body: string): void }): void {
    const xref = doc.xref;
    const copy = (n: number): Dict => {
        const d = xref.fetch(new PRef(n, 0));
        const c = new Dict(xref);
        if (d instanceof Dict) for (const k of d.keys()) c.set(k, d.raw(k)!);
        return c;
    };
    for (const [id, e] of edits) {
        const w = copy(id);
        if (!w.keys().length) continue;
        const par = w.raw("Parent");
        // the field is the widget itself, or the parent that holds its name
        const holderNum = !w.has("T") && par instanceof PRef ? par.num : id;
        const holder = holderNum === id ? w : copy(holderNum);
        if (e.remove) {
            w.set("F", (w.num("F") | 2 | 32) >>> 0);
            api.writeObj(id, serialize(w));
            continue;
        }
        let ff = holder.num("Ff");
        const bit = (mask: number, on: boolean | undefined) => { if (on === undefined) return; ff = on ? ff | mask : ff & ~mask; };
        bit(FF_REQUIRED, e.required);
        bit(FF_READONLY, e.readOnly);
        bit(FF_MULTILINE, e.multiline);
        if (e.required !== undefined || e.readOnly !== undefined || e.multiline !== undefined) holder.set("Ff", ff >>> 0);
        if (e.readOnly === false) w.set("F", w.num("F") & ~64);
        if (e.name !== undefined && e.name.trim()) holder.set("T", pdfText(e.name.split(".").pop()!.trim()));
        if (e.label !== undefined) { if (e.label) holder.set("TU", pdfText(e.label)); else holder.map.delete("TU"); }
        if (e.maxLen !== undefined) { if (e.maxLen > 0) holder.set("MaxLen", e.maxLen); else holder.map.delete("MaxLen"); }
        if (e.options) holder.set("Opt", e.options.map((o) => pdfText(o)));
        if (e.validation !== undefined) { if (e.validation && e.validation.type !== "none") holder.set("FGValidate", JSON.stringify(e.validation)); else holder.map.delete("FGValidate"); }
        if (holderNum !== id) api.writeObj(holderNum, serialize(holder));
        api.writeObj(id, serialize(w));
    }
}

// ---- saving ------------------------------------------------------------------------------------------------------------------------------

export const num = (n: number) => (Number.isInteger(n) ? String(n) : n.toFixed(3).replace(/0+$/, "").replace(/\.$/, ""));

/** A PDF text string as a byte string: Latin-1 when it fits, otherwise UTF-16BE with a byte order mark. */
export function pdfText(s: string): string {
    if (/^[\x20-\x7e\xa0-\xff]*$/.test(s)) return s;
    let r = "\xfe\xff";
    for (let i = 0; i < s.length; i++) { const c = s.charCodeAt(i); r += String.fromCharCode(c >> 8, c & 255); }
    return r;
}
const latin = (s: string): string => { let r = ""; for (const ch of s) { const c = ch.codePointAt(0)!; r += c < 256 ? ch : "?"; } return r; };
export const lit = (s: string) => "(" + [...s].map((ch) => { const c = ch.charCodeAt(0); return ch === "(" || ch === ")" || ch === "\\" ? "\\" + ch : c < 32 || c > 126 ? "\\" + c.toString(8).padStart(3, "0") : ch; }).join("") + ")";

let measureCtx: { measureText(t: string): { width: number }; font: string } | null | undefined;
function textWidth(s: string, size: number): number {
    if (measureCtx === undefined) {
        try { measureCtx = typeof OffscreenCanvas !== "undefined" ? (new OffscreenCanvas(1, 1).getContext("2d") as unknown as typeof measureCtx) : null; } catch { measureCtx = null; }
    }
    if (!measureCtx) return s.length * size * 0.52;
    measureCtx.font = `100px Helvetica, Arial, sans-serif`;
    return (measureCtx.measureText(s).width / 100) * size;
}

/** The appearance of a text-like field: its text in Helvetica inside the widget's box. */
function textAppearance(w: FormWidget, value: string): { bbox: string; content: string } {
    const bw = w.rect[2] - w.rect[0], bh = w.rect[3] - w.rect[1];
    let size = w.fontSize;
    if (!size) size = w.multiline ? 11 : Math.max(6, Math.min(12, bh - 4));
    const shown = w.password ? "*".repeat(value.length) : value;
    let lines: string[];
    if (w.multiline) {
        lines = [];
        for (const para of shown.split(/\r\n|\r|\n/)) {
            let cur = "";
            for (const word of para.split(/(\s+)/)) {
                if (cur && textWidth(cur + word, size) > bw - 4) { lines.push(cur.trimEnd()); cur = word.trimStart(); } else cur += word;
            }
            lines.push(cur);
        }
    } else lines = [shown];
    let c = `/Tx BMC\nq\n1 1 ${num(bw - 2)} ${num(bh - 2)} re W n\nBT\n/Helv ${num(size)} Tf 0 g\n`;
    let y = w.multiline ? bh - 2 - size : (bh - size) / 2 + size * 0.22;
    lines.forEach((ln) => {
        const lw = textWidth(ln, size);
        const x = w.align === 1 ? Math.max(2, (bw - lw) / 2) : w.align === 2 ? Math.max(2, bw - lw - 2) : 2;
        c += `1 0 0 1 ${num(x)} ${num(y)} Tm ${lit(latin(ln))} Tj\n`;
        y -= size * 1.2;
    });
    c += "ET\nQ\nEMC\n";
    return { bbox: `[0 0 ${num(bw)} ${num(bh)}]`, content: c };
}

/**
 * Writes `values` (field name → text, option value, or "Off" / an on-state name) into the PDF as an incremental update.
 * `write` is the same object writer the annotation save uses, so both share one numbering and cross-reference section.
 */
export async function writeFormValues(
    doc: PdfDocument,
    values: Map<string, string>,
    api: { newRef(): number; writeObj(num: number, body: string): void; writeStream(num: number, dict: string, data: Uint8Array, compress?: boolean): Promise<void> }
): Promise<void> {
    if (!values.size) return;
    const widgets = formWidgets(doc).filter((w) => values.has(w.name) && !w.readOnly);
    if (!widgets.length) return;
    const xref = doc.xref;
    const enc = new TextEncoder();
    const helv = "/Font << /Helv << /Type /Font /Subtype /Type1 /BaseFont /Helvetica /Encoding /WinAnsiEncoding >> >>";
    const fieldDone = new Set<number>();
    const wanted = new Map<string, FormWidget[]>();
    for (const w of widgets) { const l = wanted.get(w.name) ?? []; l.push(w); wanted.set(w.name, l); }

    const widgetDict = (n: number): Dict => {
        const d = xref.fetch(new PRef(n, 0));
        const c = new Dict(xref);
        if (d instanceof Dict) for (const k of d.keys()) c.set(k, d.raw(k)!);
        return c;
    };

    for (const [name, ws] of wanted) {
        const raw = values.get(name)!;
        const first = ws[0]!;
        // the field that carries /V: the widget's parent when the widget has no name of its own
        const holderOf = (n: number): { num: number; dict: Dict } => {
            const w = widgetDict(n);
            const par = w.raw("Parent");
            if (!w.has("T") && par instanceof PRef) { const pd = xref.fetch(par); if (pd instanceof Dict) { const c = new Dict(xref); for (const k of pd.keys()) c.set(k, pd.raw(k)!); return { num: par.num, dict: c }; } }
            return { num: n, dict: w };
        };
        if (first.kind === "check" || first.kind === "radio") {
            const holder = holderOf(first.id);
            const chosen = raw === "Off" ? "Off" : raw;
            holder.dict.set("V", new PName(chosen));
            if (holder.num === first.id) holder.dict.set("AS", new PName(chosen === "Off" ? "Off" : first.onState));
            if (!fieldDone.has(holder.num)) { fieldDone.add(holder.num); if (holder.num !== first.id) api.writeObj(holder.num, serialize(holder.dict)); }
            for (const w of ws) {
                if (w.id === holder.num) continue;
                const wd = widgetDict(w.id);
                wd.set("AS", new PName(chosen !== "Off" && chosen === w.onState ? w.onState : "Off"));
                api.writeObj(w.id, serialize(wd));
            }
            if (holder.num === first.id) api.writeObj(first.id, serialize(holder.dict));
            continue;
        }
        const holder = holderOf(first.id);
        holder.dict.set("V", pdfText(raw));
        if (holder.num !== first.id && !fieldDone.has(holder.num)) { fieldDone.add(holder.num); api.writeObj(holder.num, serialize(holder.dict)); }
        for (const w of ws) {
            const wd = holder.num === w.id ? holder.dict : widgetDict(w.id);
            const label = w.options.find((o) => o.value === raw)?.label ?? raw;
            const ap = textAppearance(w, label);
            const apNum = api.newRef();
            await api.writeStream(apNum, `/Type /XObject /Subtype /Form /BBox ${ap.bbox} /Resources << ${helv} >>`, enc.encode(ap.content));
            wd.set("AP", (() => { const a = new Dict(xref); a.set("N", new PRef(apNum, 0)); return a; })());
            api.writeObj(w.id, serialize(wd));
        }
    }
}
